<template>
  <div class="content klc-page">
    <!-- ============================================================
         TOOLBAR CARD: Bộ lọc + Tác vụ nhanh
         ============================================================ -->
    <div class="klc-toolbar-card row-data">
      <!-- Vùng Bộ lọc -->
      <div class="klc-toolbar-section klc-toolbar-section--filter">
        <div class="klc-card-head">
          <div>
            <p class="klc-card-title">Bộ lọc tìm kiếm</p>
            <p class="klc-card-subtitle">
              Tra cứu lịch sử học sinh: tạo mới, đổi trạng thái, chuyển lớp, chuyển nhóm...
            </p>
          </div>
        </div>

        <div class="klc-filter-grid">
          <!-- Mã học sinh -->
          <el-input
            v-model.trim="dataSearch.kidCode"
            placeholder="Mã học sinh..."
            clearable
            class="klc-search-input"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          >
            <template #append>
              <el-button icon="el-icon-search" @click="handleSearch" />
            </template>
          </el-input>

          <!-- Tên học sinh -->
          <el-input
            v-model.trim="dataSearch.kidName"
            placeholder="Tên học sinh..."
            clearable
            class="klc-search-input"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          >
            <template #append>
              <el-button icon="el-icon-search" @click="handleSearch" />
            </template>
          </el-input>

          <!-- Loại sự kiện -->
          <el-select
            v-model="dataSearch.eventType"
            clearable
            placeholder="Tất cả sự kiện"
            class="klc-filter-control"
            @change="handleSearch"
          >
            <el-option
              v-for="item in eventTypeList"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>

          <!-- Từ ngày -->
          <el-date-picker
            v-model="dataSearch.fromDate"
            type="date"
            value-format="yyyy-MM-dd"
            format="dd-MM-yyyy"
            clearable
            placeholder="Từ ngày"
            class="klc-filter-control klc-date-picker"
            @change="handleSearch"
          />

          <!-- Đến ngày -->
          <el-date-picker
            v-model="dataSearch.toDate"
            type="date"
            value-format="yyyy-MM-dd"
            format="dd-MM-yyyy"
            clearable
            placeholder="Đến ngày"
            class="klc-filter-control klc-date-picker"
            @change="handleSearch"
          />
        </div>
      </div>

      <!-- Đường kẻ ngăn cách -->
      <div class="klc-toolbar-divider" />

      <!-- Vùng Tác vụ nhanh -->
      <div class="klc-toolbar-section klc-toolbar-section--action">
        <div class="klc-card-head klc-card-head--compact">
          <div>
            <p class="klc-card-title">Tác vụ nhanh</p>
            <p class="klc-card-subtitle">Đặt lại bộ lọc về mặc định.</p>
          </div>
        </div>
        <div class="klc-action-group">
          <el-button
            icon="el-icon-refresh"
            class="klc-action-button"
            @click="resetSearch"
          >
            Đặt lại
          </el-button>
        </div>
      </div>
    </div>

    <!-- ============================================================
         TABLE CARD: Danh sách lịch sử học sinh
         ============================================================ -->
    <div class="klc-table-card row-data">
      <!-- Header bảng -->
      <div class="klc-card-head klc-table-head">
        <div>
          <p class="klc-card-title">Danh sách lịch sử học sinh</p>
          <p class="klc-card-subtitle">
            Ghi nhận các lần tạo mới, đổi trạng thái, chuyển lớp và chuyển nhóm của học sinh.
          </p>
        </div>
      </div>

      <!-- Bảng dữ liệu -->
      <div class="klc-table-wrapper">
        <el-table
          v-loading="loading"
          :data="eventList"
          border
          class="sk-table"
          highlight-current-row
          :height="$tableMaxHeight || 700"
          empty-text="Chưa có lịch sử phù hợp"
          style="width: 100%"
        >
          <!-- Cột STT -->
          <el-table-column
            label="STT"
            type="index"
            width="55"
            align="center"
            :index="indexMethod"
          />

          <!-- Từ ngày -->
          <el-table-column label="Từ ngày" width="120" align="center">
            <template slot-scope="{ row }">
              <span>{{ formatDate(row.startDate || row.effectiveDate) }}</span>
            </template>
          </el-table-column>

          <!-- Đến ngày -->
          <el-table-column label="Đến ngày" width="120" align="center">
            <template slot-scope="{ row }">
              <span>{{ formatEndDate(row.endDate) }}</span>
            </template>
          </el-table-column>

          <!-- Mã HS -->
          <el-table-column prop="kidCode" label="Mã HS" width="125" align="center" />

          <!-- Học sinh -->
          <el-table-column prop="kidName" label="Học sinh" min-width="180">
            <template slot-scope="{ row }">
              <span class="klc-student-name">{{ row.kidName }}</span>
            </template>
          </el-table-column>

          <!-- Sự kiện -->
          <el-table-column label="Sự kiện" width="165" align="center">
            <template slot-scope="{ row }">
              <el-tag :type="eventTagType(row.eventType)" size="small" effect="light" class="klc-tag">
                {{ eventLabel(row.eventType) }}
              </el-tag>
            </template>
          </el-table-column>

          <!-- Nội dung thay đổi -->
          <el-table-column label="Nội dung thay đổi" min-width="280">
            <template slot-scope="{ row }">
              <span class="klc-event-desc">{{ eventDescription(row) }}</span>
            </template>
          </el-table-column>

          <!-- Người thao tác -->
          <el-table-column label="Người thao tác" min-width="140" align="center">
            <template slot-scope="{ row }">
              <span>{{ actorLabel(row.modifiedByName, row.lastModifieBy) }}</span>
            </template>
          </el-table-column>

          <!-- Thời gian thao tác -->
          <el-table-column label="Thời gian thao tác" width="165" align="center">
            <template slot-scope="{ row }">
              <span>{{ formatDateTime(row.lastModifieDate || row.effectiveAt || row.createdDate) }}</span>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- Phân trang -->
      <div class="klc-pagination">
        <el-pagination
          background
          :current-page.sync="pageNumber"
          :page-sizes="$pageListDefaultNew"
          :page-size.sync="maxPageItem"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import KidsLifecycleService from "@/services/KidsService/KidsLifecycleService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";

export default {
  name: "KidsLifecycle",
  data() {
    return {
      loading: false,
      pageNumber: 1,
      // Dùng cùng danh sách lựa chọn số dòng với các màn hình danh sách khác.
      maxPageItem: this.$pageSizeDefaultNew,
      total: 0,
      eventList: [],
      dataSearch: {
        kidCode: "",
        kidName: "",
        eventType: "",
        fromDate: "",
        toDate: "",
      },
      // Không đưa "Nghỉ hẳn" vào danh mục vì nghiệp vụ hiện tại không sử dụng trạng thái này.
      eventTypeList: [
        { value: "CREATED", label: "Tạo mới" },
        { value: "STATUS_CHANGED", label: "Đổi trạng thái" },
        { value: "CLASS_CHANGED", label: "Chuyển lớp" },
        { value: "GROUP_CHANGED", label: "Chuyển nhóm" },
        { value: "GROUP_ADDED", label: "Thêm vào nhóm" },
        { value: "GROUP_REMOVED", label: "Ra khỏi nhóm" },
        { value: "RESTORED", label: "Khôi phục hồ sơ" },
        { value: "DELETED", label: "Xóa hồ sơ" },
      ],
      statusLabelMap: {
        STUDYING: "Đang học",
        STUDY_WAIT: "Chờ học",
        RESERVE: "Bảo lưu",
        LEAVE_SCHOOL: "Nghỉ học",
        OUT_SCHOOL: "Ra trường",
        DELETE: "Đã xóa",
      },
    };
  },
  created() {
    // Khi đi từ danh sách học sinh, nhận kidCode trên URL để mở đúng lịch sử
    // của học sinh được chọn thay vì tải toàn bộ lịch sử của trường.
    const kidCode = this.$route && this.$route.query ? this.$route.query.kidCode : "";
    if (kidCode) {
      this.dataSearch.kidCode = kidCode;
    }
    this.searchEvents();
  },
  methods: {
    indexMethod(index) {
      return (this.pageNumber - 1) * this.maxPageItem + index + 1;
    },

    /** Gọi API theo trang hiện tại; tất cả bộ lọc đều được gửi cùng một request. */
    async searchEvents() {
      this.loading = true;
      try {
        const params = {
          pageNumber: this.pageNumber,
          maxPageItem: this.maxPageItem,
          kidCode: this.dataSearch.kidCode || undefined,
          kidName: this.dataSearch.kidName || undefined,
          eventType: this.dataSearch.eventType || undefined,
          fromDate: this.dataSearch.fromDate || undefined,
          toDate: this.dataSearch.toDate || undefined,
        };
        // Loại bỏ key rỗng / null / undefined trước khi gửi
        Object.keys(params).forEach(k => {
          if (params[k] === "" || params[k] === null || params[k] === undefined) delete params[k];
        });

        const response = await KidsLifecycleService.search(params);
        const payload = response && response.data ? response.data : {};
        this.eventList = Array.isArray(payload.data) ? payload.data : [];
        this.total = Number(payload.total) || 0;
      } catch (err) {
        this.eventList = [];
        this.total = 0;
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Không tải được lịch sử học sinh"
        );
      } finally {
        this.loading = false;
      }
    },

    /** Khi đổi điều kiện tìm kiếm, quay về trang đầu để không rơi vào trang rỗng. */
    handleSearch() {
      this.pageNumber = 1;
      this.searchEvents();
    },

    handlePageChange(page) {
      this.pageNumber = page;
      this.searchEvents();
    },

    /** Khi đổi số dòng/trang, quay về trang đầu để tránh trang hiện tại không còn dữ liệu. */
    handleSizeChange(maxPageItem) {
      this.maxPageItem = maxPageItem;
      this.pageNumber = 1;
      this.searchEvents();
    },

    resetSearch() {
      this.dataSearch = {
        kidCode: "",
        kidName: "",
        eventType: "",
        fromDate: "",
        toDate: "",
      };
      this.pageNumber = 1;
      this.searchEvents();
    },

    eventLabel(type) {
      const item = this.eventTypeList.find(x => x.value === type);
      return item ? item.label : type || "Sự kiện khác";
    },

    eventTagType(type) {
      const tagMap = {
        CREATED: "success",
        STATUS_CHANGED: "warning",
        CLASS_CHANGED: "primary",
        GROUP_CHANGED: "primary",
        GROUP_ADDED: "success",
        GROUP_REMOVED: "danger",
        RESTORED: "success",
        DELETED: "danger",
      };
      return tagMap[type] || "info";
    },

    statusLabel(status) {
      return this.statusLabelMap[status] || status || "-";
    },

    /** API đã đổi idModified thành tên; fallback giữ tương thích dữ liệu cũ. */
    actorLabel(modifiedByName, lastModifieBy) {
      if (modifiedByName) {
        return modifiedByName;
      }
      if (lastModifieBy === "SYSTEM") {
        return "Hệ thống";
      }
      return lastModifieBy || "Không xác định";
    },

    /** Chuyển các cặp old/new thành câu ngắn để người dùng đọc ngay trên bảng. */
    eventDescription(event) {
      if (event.eventType === "CREATED") {
        return `Trạng thái: ${this.statusLabel(event.newStatus)}${this.formatTransitionTarget(event.newClassName, "Lớp")}`;
      }
      if (event.eventType === "STATUS_CHANGED") {
        return `${this.statusLabel(event.oldStatus)} → ${this.statusLabel(event.newStatus)}`;
      }
      if (event.eventType === "CLASS_CHANGED") {
        return `${event.oldClassName || "-"} → ${event.newClassName || "-"}`;
      }
      if (event.eventType === "GROUP_CHANGED") {
        return `${event.oldGroupName || "-"} → ${event.newGroupName || "-"}`;
      }
      if (event.eventType === "GROUP_ADDED") {
        return `Thêm vào nhóm: ${event.newGroupName || "-"}`;
      }
      if (event.eventType === "GROUP_REMOVED") {
        return `Ra khỏi nhóm: ${event.oldGroupName || "-"}`;
      }
      if (event.eventType === "RESTORED") {
        return `Khôi phục hồ sơ, trạng thái hiện tại: ${this.statusLabel(event.newStatus)}`;
      }
      if (event.eventType === "DELETED") {
        return "Hồ sơ được xóa mềm";
      }
      return event.note || "-";
    },

    formatTransitionTarget(value, label) {
      return value ? `, ${label}: ${value}` : "";
    },

    formatDate(value) {
      if (!value) return "-";
      const parts = String(value).substring(0, 10).split("-");
      return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : value;
    },

    /** endDate null nghĩa là event/khoảng đó vẫn đang có hiệu lực. */
    formatEndDate(value) {
      return value ? this.formatDate(value) : "Hiện tại";
    },

    formatDateTime(value) {
      if (!value) return "-";
      return String(value).replace("T", " ").substring(0, 19);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/sk-table.scss";

/* ─── .klc-page ─────────────────────────────────── */
.klc-page {
  padding: 4px 0 16px;
  .row-data {
    margin-bottom: 20px;
  }
}

/* ─── Toolbar card ─────────────────────────────── */
.klc-toolbar-card {
  display: flex;
  align-items: flex-start;
  gap: 18px;
  padding: 18px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
}

.klc-toolbar-section {
  min-width: 0;
}

.klc-toolbar-section--filter {
  flex: 1;
}

.klc-toolbar-section--action {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.klc-toolbar-divider {
  width: 1px;
  align-self: stretch;
  background: #e2e8f0;
  flex-shrink: 0;
  margin: 0 4px;
}

/* ─── Table card ───────────────────────────────── */
.klc-table-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
  padding: 18px 18px 14px;
}

/* ─── Card head ────────────────────────────────── */
.klc-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.klc-card-head--compact {
  margin-bottom: 14px;
}

.klc-table-head {
  align-items: center;
}

.klc-card-title {
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

.klc-card-subtitle {
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: #64748b;
}

/* ─── Filter grid ──────────────────────────────── */
.klc-filter-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.klc-search-input {
  width: 210px;
}

.klc-filter-control {
  width: 175px;
}

.klc-date-picker {
  width: 160px;
}

/* ─── Action group ─────────────────────────────── */
.klc-action-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.klc-action-button {
  min-width: 110px;
  font-weight: 600;
}

/* ─── Table wrapper (border-radius) ───────────── */
.klc-table-wrapper {
  border-radius: 14px;
  overflow: hidden;
}

.klc-student-name {
  font-weight: 600;
  color: #1e293b;
}

.klc-tag {
  font-weight: 600;
  border-radius: 6px;
}

.klc-event-desc {
  font-size: 13px;
  color: #334155;
  line-height: 1.4;
}

/* ─── Tiêu đề cột bảng: nền xanh primary #2563eb ─ */
/deep/ .sk-table thead th,
/deep/ .sk-table thead td {
  background: #2563eb !important;
  color: #ffffff !important;
  font-weight: 700;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

/deep/ .sk-table thead th > .cell,
/deep/ .sk-table thead td > .cell {
  color: #ffffff !important;
  font-weight: 700;
}

/deep/ .sk-table thead tr {
  height: 56px;
}

/deep/ .sk-table tbody td {
  background: #ffffff;
}

/* ─── Phân trang ───────────────────────────────── */
.klc-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

/* ─── Input / Button bo góc (override Element UI) ─ */
/deep/ .klc-page .el-input__inner,
/deep/ .klc-page .el-select .el-input__inner {
  border-radius: 10px;
}

/deep/ .klc-page .el-button {
  border-radius: 10px;
}

/deep/ .klc-page .el-input-group__append .el-button {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

/* ─── Responsive ───────────────────────────────── */
@media (max-width: 1200px) {
  .klc-toolbar-card {
    flex-direction: column;
    align-items: stretch;
  }

  .klc-toolbar-divider {
    width: 100%;
    height: 1px;
    margin: 4px 0;
  }

  .klc-table-head {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

@media (max-width: 768px) {
  .klc-filter-grid,
  .klc-action-group {
    flex-direction: column;
    align-items: stretch;
  }

  .klc-search-input,
  .klc-filter-control,
  .klc-date-picker,
  .klc-action-button {
    width: 100%;
  }
}
</style>
